import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es-mx';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es-mx');

const TZ = 'America/Mexico_City';

/**
 * Convierte un día del calendario (`YYYY-MM-DD`, el que teclea el operador) al INSTANTE ISO en que
 * ese día empieza y termina en el piso de venta.
 *
 * Existen porque un filtro de fechas contra `soldAt` compara **timestamps completos**, no días:
 * `soldAt` vale `2026-07-28T22:15:00.000Z` y la comparación en DynamoDB es lexicográfica, así que
 * `soldAt <= '2026-07-28'` es FALSO para toda venta de ese día — el rango "hoy" devolvía cero.
 *
 * Y van en `America/Mexico_City`, no en la zona del navegador, para que el rango case con lo que la
 * tabla muestra (`formatDateTimeMx` pinta en esa misma zona). Un día mexicano cruza dos fechas UTC:
 * el 28/07 local va de `2026-07-28T06:00:00Z` a `2026-07-29T05:59:59.999Z`.
 */
export function mxDayStartUtc(day: string): string {
    return dayjs.tz(day, TZ).startOf('day').toISOString();
}

export function mxDayEndUtc(day: string): string {
    return dayjs.tz(day, TZ).endOf('day').toISOString();
}

/**
 * El día de calendario (`YYYY-MM-DD`) al que pertenece un instante **en el piso de venta**.
 *
 * Es la contraparte de `mxDayStartUtc`/`mxDayEndUtc` para filtrar en memoria. Existe porque
 * `iso.slice(0, 10)` da el día **UTC**, y un instante como `2026-08-01T00:06:00Z` es el 31/jul en
 * CDMX: la tabla pinta "31/jul" (vía `formatDateMx`, que sí usa la zona) y el filtro lo trataría
 * como 01/ago. Filtrar "hasta el 31/jul" hacía desaparecer una fila que decía 31/jul.
 */
export function mxDay(iso: string): string {
    return dayjs(iso).tz(TZ).format('YYYY-MM-DD');
}

/** Hora y minuto de un instante en el piso de venta (CDMX), sin importar la zona del navegador. */
export function mxClock(now: Date): { hours: number; minutes: number } {
    const local = dayjs(now).tz(TZ);
    return { hours: local.hour(), minutes: local.minute() };
}

/** El día de calendario en el piso de venta de hace `days` días — los atajos Hoy/7d/30d/Trim. */
export function mxDayOffset(days: number): string {
    return dayjs().tz(TZ).subtract(days, 'day').format('YYYY-MM-DD');
}

/** Corre un `YYYY-MM-DD` `days` días de calendario: negativo atrás, positivo adelante. */
export function dayOffset(day: string, days: number): string {
    const date = new Date(`${day}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

/**
 * Días de CALENDARIO entre dos instantes, en el piso de venta. Nunca negativo.
 *
 * Cuenta días del calendario, no bloques de 24 horas: una compra de ayer a las 18:00 vista hoy al
 * mediodía son 18 horas, y `floor(18 / 24)` da **0** — la ficha decía "02/ago/2026 · hace 0 d"
 * teniendo hoy 3 de agosto. El operador lee la fecha y el "hace N" juntos; que no cuadren le hace
 * dudar de las dos.
 *
 * `now` entra por parámetro para las pantallas que se dibujan contra un instante fijo.
 */
export function daysBetweenMx(iso: string, now: Date | string): number {
    const from = dayjs(iso).tz(TZ).startOf('day');
    const to = dayjs(now).tz(TZ).startOf('day');
    return Math.max(0, to.diff(from, 'day'));
}

/** Los días de calendario que pasaron entre un instante y hoy. */
export function daysSinceMx(iso: string): number {
    return daysBetweenMx(iso, new Date());
}

/**
 * Formato de fecha del producto: `DD/MMM/YYYY`, con el mes en LETRAS — `17/jun/2026`.
 *
 * El mes va en letras a propósito: `03/08/2026` es ambiguo para quien lee de reojo (¿3 de agosto
 * u 8 de marzo?), y estas tablas se leen así todo el día. El locale `es-mx` ya está cargado
 * arriba, así que `MMM` sale en español y sin punto.
 */
export function formatDateMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('DD/MMM/YYYY');
}

/**
 * Mismo `DD/MMM/YYYY` pero para un DÍA de calendario suelto (`YYYY-MM-DD`), como la fecha de
 * nacimiento: no hay instante que ubicar, y convertirlo de zona lo corre un día hacia atrás en
 * cualquier navegador al este de CDMX.
 */
export function formatDayMx(day: string): string {
    return dayjs(day).format('DD/MMM/YYYY');
}

/**
 * Día y mes en letras, sin año (`09/jun`), para las columnas angostas de una tabla compacta.
 * El mes sigue en letras: es lo que evita leer `09/06` como 6 de septiembre.
 */
export function formatShortDateMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('DD/MMM');
}

/**
 * El mismo `DD/MMM` pero para un DÍA de calendario suelto (`YYYY-MM-DD`), como los bordes del corte
 * semanal: no hay instante que ubicar, y convertirlo de zona lo corre un día hacia atrás.
 */
export function formatShortDayMx(day: string): string {
    return dayjs(day).format('DD/MMM');
}

/** El mismo DÍA con año (`05 mar 25`): un plan de 52 semanas cruza el año y sin él dos filas se leen igual. */
export function formatShortDayWithYearMx(day: string): string {
    return dayjs(day).format('DD MMM YY');
}

/** Hora del piso de venta (`HH:mm`, 24 h) en la zona de la tienda, para las filas densas. */
export function formatTimeMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('HH:mm');
}

/**
 * La misma hora CON segundos, para ventanas de menos de un minuto: truncar a `HH:mm` puede pintar
 * como pasada una hora que todavía no llega.
 */
export function formatTimeSecondsMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('HH:mm:ss');
}

/**
 * La ventana «hoy»: el día de calendario completo en CDMX, de borde a borde.
 * `to` presente es lo que hace que el silo calcule la comparación contra la semana pasada.
 */
export function mxTodayWindow(now: Date = new Date()): { from: string; to: string } {
    const today = mxDay(now.toISOString());
    return { from: mxDayStartUtc(today), to: mxDayEndUtc(today) };
}

/**
 * La ventana de los últimos `days` días de CALENDARIO en el piso de venta, hoy incluido.
 * `mxLastDaysWindow(7)` va del arranque de hace 6 días al cierre de hoy — los «últimos 7 días».
 */
export function mxLastDaysWindow(
    days: number,
    now: Date = new Date(),
): { from: string; to: string } {
    const today = mxDay(now.toISOString());
    const first = dayjs
        .tz(today, TZ)
        .subtract(days - 1, 'day')
        .format('YYYY-MM-DD');
    return { from: mxDayStartUtc(first), to: mxDayEndUtc(today) };
}

/** Días de calendario entre dos días `YYYY-MM-DD`. Negativo cuando el primero ya pasó. */
export function daysBetweenDays(day: string, fromDay: string): number {
    return dayjs(day).diff(dayjs(fromDay), 'day');
}

/** `26 ago 26` — el sello corto de un timeline, donde la hora no aporta y el ancho es poco. */
export function formatTimelineDateMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('DD MMM YY');
}

export function formatDateTimeMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('DD/MMM/YYYY HH:mm');
}

/**
 * El mismo sello CON segundos, para casar una llamada nuestra con el log del proveedor. Va aparte
 * de `formatDateTimeMx` porque las 16 pantallas que lo usan no pidieron los segundos.
 */
export function formatDateTimeSecondsMx(iso: string): string {
    return dayjs(iso).tz(TZ).format('DD/MMM/YYYY HH:mm:ss');
}

export function formatRelativeMx(iso: string): string {
    return new Intl.RelativeTimeFormat('es-MX', { numeric: 'auto' }).format(
        Math.round((dayjs(iso).valueOf() - Date.now()) / 1000 / 60),
        'minute',
    );
}
